import { httpApi } from '@app/api/http.api';
import { IDealRoomExternal } from '@app/interfaces/deal-room';
import {
  IDealRoomInfo,
  IDealRoomInfoExportFile,
  IDealRoomInfoFile,
  IDealRoomInfoFileDownload,
  IDealRoomInfoFileUpload,
  IDealRoomInfoUnreadFile,
} from '@app/interfaces/deal-room-info';

const baseSlug = '/deal-room-info';

export const getDealRoomInfo = async (dealRoomId: string): Promise<IDealRoomInfo> => {
  try {
    const { data } = await httpApi.get<IDealRoomInfo>(`${baseSlug}/${dealRoomId}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const createDealRoomInfo = async (payload: Partial<IDealRoomInfo>): Promise<IDealRoomInfo> => {
  try {
    const { data } = await httpApi.post<IDealRoomInfo>(`${baseSlug}`, payload);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateDealRoomInfo = async (id: string, payload: Partial<IDealRoomInfo>): Promise<IDealRoomInfo> => {
  try {
    const { data } = await httpApi.patch<IDealRoomInfo>(`${baseSlug}/${id}`, payload);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const downloadDealRoomInfoFile = async (
  dealRoomId: string,
  fileId: string,
  payload: Partial<IDealRoomInfoFileDownload>,
): Promise<IDealRoomInfoExportFile> => {
  try {
    const { data } = await httpApi.post<IDealRoomInfoExportFile>(
      `${baseSlug}/download/${dealRoomId}/${fileId}`,
      payload,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomInfoFiles = async (dealRoomId: string, isExternal = true): Promise<IDealRoomInfoFile[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomInfoFile[]>(`${baseSlug}/files/${dealRoomId}?isExternal=${isExternal}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomInfoExternalFiles = async (
  dealRoomId: string,
  dealRoomPeopleId: string,
  isExternal = true,
  query: IDealRoomExternal,
): Promise<IDealRoomInfoFile[]> => {
  try {
    const { data } = await httpApi.get<IDealRoomInfoFile[]>(
      `${baseSlug}/externalFiles/${dealRoomId}/${dealRoomPeopleId}`,
      {
        params: {
          ...query,
          isExternal,
        },
      },
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getDealRoomUnreadFile = async (
  dealRoomId: string,
  userId: string | null,
  dealRoomPeopleId: string | null,
): Promise<IDealRoomInfoUnreadFile> => {
  try {
    const { data } = await httpApi.post<IDealRoomInfoUnreadFile>(`${baseSlug}/unreadFile/${dealRoomId}`, {
      userId,
      dealRoomPeopleId,
    });
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const uploadDealRoomInfoExternalFile = async (
  dealRoomId: string,
  payload: Partial<IDealRoomInfoFileUpload>,
): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>(`${baseSlug}/uploadExternal/${dealRoomId}`, payload);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const uploadDealRoomInfoFile = async (
  dealRoomId: string,
  payload: Partial<IDealRoomInfoFileUpload>,
): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>(`${baseSlug}/upload/${dealRoomId}`, payload);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const deleteDealRoomInfoFile = async (
  dealRoomId: string,
  fileId: string,
  tabId: string | null,
): Promise<undefined> => {
  try {
    const { data } = await httpApi.delete<undefined>(`${baseSlug}/delete/${dealRoomId}/${fileId}?tabId=${tabId}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const deleteExternalDealRoomInfoFile = async (
  dealRoomId: string,
  fileId: string,
  dealRoomPeopleId: string,
  tabId: string | null,
): Promise<undefined> => {
  try {
    const { data } = await httpApi.delete<undefined>(
      `${baseSlug}/deleteExternal/${dealRoomId}/${fileId}/${dealRoomPeopleId}?tabId=${tabId}`,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateDealRoomInfoOpening = async (dealRoomId: string, opening: string | null): Promise<IDealRoomInfo> => {
  try {
    const { data } = await httpApi.post<IDealRoomInfo>(`${baseSlug}/updateDealRoomInfoOpening/${dealRoomId}`, {
      opening,
    });
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};
